/* eslint-disable max-lines */
import { Reducer } from 'redux';
import { CloudServiceProvider } from '../../../constants/AppConstants';
import { buildDeployMenu } from '../../../pages/Dashboard/DashboardCover/BuildDeploy/BuildDeployConstants';
import { envTypes } from '../../../constants/ModalConstants';
import { ICountLandingZone } from '../../../components/layout/HomePage/HomePageConstants';
import {
  IAllEnvironmentsRespObj,
  IBuildDeployErrorState,
  IFetchCategoryAndTechDomainResponse,
  ILandingZoneSector,
  IWorkloadPatternRespObj,
  LandingZoneStatus,
} from '../../models/BuildDeployModels';
import {
  ICheckMicroServicesPatternStatus,
  IConfigureMicroServiceError,
} from '../../models/DashboardModels';
import {
  BuildDeployAction,
  BuildDeployActionTypes,
  IConfigureLandingZoneError,
  ILandingZoneErrorResponse,
} from '../actions/BuildDeployActions';
import BuildDeployState, {
  IArchitectureCatalogueResponse,
  ICloudLandingZoneKeysResponse,
  IConfigureLZLoadingPayload,
  IDeploymentLogsResponse,
  IDeploymentsResponse,
  IEnvironmentsDeployed,
  ILandingZoneDeploymentStatus,
  ILandingZoneState,
  IPipelinesAnalyticsResponse,
  IPipelinesCummulativeAnalyticsResponse,
  IYamlContent,
  IDeployValueState,
  ILandingZoneDeploymentResponse,
  IIndustryPolicy,
  IIndustryCompliance,
  IGenAiCreateResponse,
  IValidateYamlPayload,
  IWorkFLowLogsResponse,
  ICustomCloudTemplateDeploymentInfo,
  IUploadCustomArchitectureFilePayload,
  IUploadCustomCloudConfigurationFilePayload,
  ICloudBuildConfigVariable,
  IGenerateArchitectureDiagramsResponse,
  IGeneratedArchitectureDiagramHistory,
  IUploadCustomCloudConfigurationFileResponse,
  IGeneratedArchitectureDiagramHistoryStackItem,
  IConvertArchitectureDiagramsResponse,
  IConvertArchitectureDiagramsResponseWithType,
} from '../state/BuildDeployState';

export const initialState: BuildDeployState = {
  error: '',
  deployments: [] as IDeploymentsResponse[],
  loading: false,
  loadingError: false,
  landingZonesError: undefined,
  pipelinesCummulative: [] as IPipelinesCummulativeAnalyticsResponse[],
  environmentsDeployed: [] as IEnvironmentsDeployed[],
  architectureList: [] as IArchitectureCatalogueResponse[],
  pipelinesAnalytics: [] as IPipelinesAnalyticsResponse[],
  yamlContent: {} as IYamlContent,
  yamlDefaultTemplateContent: '',
  industryYamlContent: '',
  allWorkloadPatternsAvailable: [],
  totalEnvironments: {
    all: 0,
    gcp: 0,
    aws: 0,
    azure: 0,
    oracle: 0,
    openshift_aws: 0,
    openshift_azure: 0,
    multicloud: 0,
  },
  landingZoneDeploymentStatus: [] as ILandingZoneDeploymentStatus[],
  landingZones: [],
  buildYaml: '',
  selectedTab: buildDeployMenu[0].menuName,
  cloudLandingZoneKeys: [] as ICloudLandingZoneKeysResponse[],
  deployInstanceId: '',
  cloudLandingZoneDeployStatus: {
    state: '',
    failed: false,
  } as ILandingZoneDeploymentResponse,
  deploymentLogs: [] as IDeploymentLogsResponse[],
  deployValues: {
    cloudEnvName: '',
    environmentName: '',
    environmentType: '',
    selectedProvider: CloudServiceProvider.AWS,
    selectedEnvType: envTypes.DEFAULT_TEMPLATE.name,
    defaultProviderSelected: false,
    multiCloudCluster: { 'default': ['XYZ'] },
    projectName: '',
    multiActiveCloud: 'default',
    multiCloudClusterForm: {},
    highlightedCluster: ['anthos_gcp'],
    industryYamlPolicies: [],
    iac: 'terraform',
  } as IDeployValueState,
  multiCloudYamlContent:
    'Environments: capabilities_multicloud: anthos_gcp: - bastion_name: bastionname cluster_tags: sd enable_feature: truegke_cluster_name: newvaluesmaster_ipv4_cidr_block: 10.0.3.0/28max_node_count: 4max_pods_per_node: 8min_node_count: 3',
  previewYamlBase64Loading: false,
  previewYamlBase64String: '',
  allEnvironments: [],
  loadingCount: 0,
  validateYaml: {
    loading: false,
    data: {
      logs: '',
      planId: '',
    },
  },
  validateLandingZone: { validation: '' },
  industryPolicies: [],
  industryCompliances: [],
  architectureDiagram: {
    downloadContent: '',
    category: [],
    domains: [],
  },
  generatedArchitectures: {
    generatedArchitecturesLoading: false,
    generatedArchitectureDiagram: {
      content: '',
      imageUrl: '',
      techPlatform: '',
      architectureName: '',
      category: '',
      techDomain: '',
      description: '',
      relevantUsecase: '',
      diagramType: 'mermaid',
    } as IGeneratedArchitectureDiagramHistoryStackItem,
    generatedArchitectureDiagramHistory: {
      stack: [],
      currentIndex: -1,
    } as IGeneratedArchitectureDiagramHistory,
  },
  industryPoliciesYaml: {} as {
    [key: string]: string;
  },
  terraformCode: '',
  genAiCreate: {
    services: {},
    terraformFolderName: '',
  },
  workFlowLogs: {} as IWorkFLowLogsResponse,
  customArchitectureInfo: {
    uploadedJsonFile: '',
    response: null,
  },
  customCloudTemplateDeploymentInfo: {
    uploadedZipFile: null,
    landingZoneId: null,
    branch: null,
  } as ICustomCloudTemplateDeploymentInfo,
  cloudBuildConfigVariables: [] as ICloudBuildConfigVariable[],
};

export const buildDeployReducer: Reducer<BuildDeployState> = (
  state = initialState,
  action: BuildDeployAction,
) => {
  switch (action.type) {
    case BuildDeployActionTypes.ADD_LANDING_ZONE:
      return {
        ...state,
        landingZones: [action.data as ILandingZoneState, ...state.landingZones],
      };
    case BuildDeployActionTypes.GET_BUILD_DEPLOY_DETAILS_SUCCESS:
      return {
        ...state,
        landingZones: action.data as Array<ILandingZoneState>,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };

    case BuildDeployActionTypes.GET_BUILD_DEPLOY_DETAILS_ERROR:
    case BuildDeployActionTypes.DELETE_LANDING_ZONE_ERROR:
    case BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_STATUS_ERROR:
    case BuildDeployActionTypes.GET_DEPLOYMENT_DETAILS_ERROR:
    case BuildDeployActionTypes.GET_ALL_ENVIRONMENTS_ERROR:
    case BuildDeployActionTypes.GET_DEPLOYMENT_AUDIT_ERROR:
    case BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_DETAILED_LOGS_ERROR:
    case BuildDeployActionTypes.DOWNLOAD_ARCHITECTURE_DIAGRAM_ERROR:
    case BuildDeployActionTypes.FETCH_CATEGORY_AND_TECHDOMAIN_OPTIONS_ERROR:
    case BuildDeployActionTypes.GET_WORKFLOW_LOGS_ERROR:
    case BuildDeployActionTypes.UPLOAD_ARCHITECTURE_ERROR:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        error: action.data as Error,
      };

    case BuildDeployActionTypes.ENABLE_LANDING_ZONE_STARTED:
      return {
        ...state,
      };

    case BuildDeployActionTypes.ENABLE_LANDING_ZONE_SUCCESS:
      return {
        ...state,
        landingZones: [
          ...state.landingZones.slice(0, action.data as number),
          {
            ...state.landingZones[action.data as number],
            landingZoneEnabled: true,
          },
          ...state.landingZones.slice((action.data as number) + 1),
        ],
      };

    case BuildDeployActionTypes.DISABLE_LANDING_ZONE:
      return {
        ...state,
        landingZones: [
          ...state.landingZones.slice(0, action.data as number),
          {
            ...state.landingZones[action.data as number],
            landingZoneEnabled: false,
          },
          ...state.landingZones.slice((action.data as number) + 1),
        ],
      };

    case BuildDeployActionTypes.ENABLE_LANDING_ZONE_ERROR:
    case BuildDeployActionTypes.CONFIGURE_ETL_PATTERN_ERROR:
    case BuildDeployActionTypes.DESTROY_WORKLOAD_PATTERN_ERROR:
    case BuildDeployActionTypes.ENABLE_MICROSERVIVES_PATTERN_ERROR:
    case BuildDeployActionTypes.ENABLE_ETL_PATTERN_ERROR:
      return {
        ...state,
        error: action.data as Error,
      };

    case BuildDeployActionTypes.ENABLE_MICROSERVIVES_PATTERN_STARTED:
      return {
        ...state,
      };

    case BuildDeployActionTypes.ENABLE_MICROSERVIVES_PATTERN_SUCCESS:
      return {
        ...state,
        landingZones: [
          ...state.landingZones.slice(0, action.data as number),
          {
            ...state.landingZones[action.data as number],
            workLoadPatternMicroServicesEnabled: state.landingZones[action.data as number]
              .workLoadPatternMicroServicesConfigured
              ? true
              : !state.landingZones[action.data as number].workLoadPatternMicroServicesEnabled,
          },
          ...state.landingZones.slice((action.data as number) + 1),
        ],
      };

    case BuildDeployActionTypes.ENABLE_ETL_PATTERN_STARTED:
      return {
        ...state,
      };

    case BuildDeployActionTypes.ENABLE_ETL_PATTERN_SUCCESS:
      return {
        ...state,
        landingZones: [
          ...state.landingZones.slice(0, action.data as number),
          {
            ...state.landingZones[action.data as number],
            workLoadPatternEtlEnabled: state.landingZones[action.data as number]
              .workLoadPatternEtlConfigured
              ? true
              : !state.landingZones[action.data as number].workLoadPatternEtlEnabled,
          },
          ...state.landingZones.slice((action.data as number) + 1),
        ],
      };

    case BuildDeployActionTypes.SET_CLOUD_LANDING_ZONE_KEYS_STARTED:
    case BuildDeployActionTypes.GET_PIPELINES_CUMMULATIVE_ANALYTICS_STARTED:
    case BuildDeployActionTypes.GET_YAML_CONTENT_STARTED:
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_CONTENT_STARTED:
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_CONTENT_FOR_CLOUD_STARTED:
    case BuildDeployActionTypes.GET_INDUSTRY_YAML_CONTENT_STARTED:
    case BuildDeployActionTypes.GET_CLOUD_LANDING_ZONE_KEYS_STARTED:
    case BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_STARTED:
    case BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_THROUGH_CONSOLE_STARTED:
    case BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_STATUS_STARTED:
    case BuildDeployActionTypes.DELETE_LANDING_ZONE_STARTED:
    case BuildDeployActionTypes.DEPLOY_LANDING_ZONE_STARTED:
    case BuildDeployActionTypes.GET_MULTICLOUD_YAML_CONTENT_STARTED:
    case BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_DETAILED_LOGS_STARTED:
    case BuildDeployActionTypes.GET_DEPLOYMENT_DETAILS_STARTED:
    case BuildDeployActionTypes.SET_ARCHTECTURE_DIAGRAM_STARTED:
    case BuildDeployActionTypes.GET_PIPELINES_ANALYTICS_STARTED:
    case BuildDeployActionTypes.LANDING_ZONES_DEPLOYMENT_STATUS_START:
    case BuildDeployActionTypes.CREATE_ENVIRONMENT_STARTED:
    case BuildDeployActionTypes.CREATE_LANDING_ZONE_YAML_START:
    case BuildDeployActionTypes.ENVIRONMENTS_DEPLOYED_START:
    case BuildDeployActionTypes.GET_ARCHITECTURE_LIST_STARTED:
    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICIES_STARTED:
    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICIES_FOR_CLOUD_STARTED:
    case BuildDeployActionTypes.FETCH_INDUSTRY_COMPLIANCES_STARTED:
    case BuildDeployActionTypes.DOWNLOAD_ARCHITECTURE_DIAGRAM_START:
    case BuildDeployActionTypes.FETCH_CATEGORY_AND_TECHDOMAIN_OPTIONS_START:
    case BuildDeployActionTypes.UPLOAD_ARCHITECTURE_START:
    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICY_YAML_START:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_SERVICES_STARTED:
    case BuildDeployActionTypes.GET_WORKFLOW_LOGS_STARTED:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_YAML_CONTENT_STARTED:
      return {
        ...state,
        loading: true,
        loadingCount: state.loadingCount + 1,
      };
    case BuildDeployActionTypes.GENERATE_ARCHITECTURE_DIAGRAMS_STARTED:
      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitectureDiagram: {
            content:'',
            diagramsImageUrl: '',
            techPlatform: '',
            architectureName: '',
            category: '',
            techDomain: '',
            description: '',
            relevantUsecase: '',
            diagramType:'mermaid'
          },
          generatedArchitecturesLoading: true,
        },
      };
    case BuildDeployActionTypes.CONVERT_GENERATED_ARCHITECTURE_DIAGRAMS_STARTED:
    case BuildDeployActionTypes.REVISE_GENERATED_ARCHITECTURE_DIAGRAMS_STARTED:
      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: true,
        },
      };
    case BuildDeployActionTypes.GENERATE_ARCHITECTURE_DIAGRAMS_ERROR:
      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: false,
        },
      };
    case BuildDeployActionTypes.CONVERT_GENERATED_ARCHITECTURE_DIAGRAMS_ERROR:
    case BuildDeployActionTypes.REVISE_GENERATED_ARCHITECTURE_DIAGRAMS_ERROR:
      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: false,
        },
      };
    case BuildDeployActionTypes.GET_BUILD_DEPLOY_DETAILS_STARTED:
      return {
        ...state,
        loading: true,
        loadingCount: state.loadingCount + 1,
        landingZones: [],
      };
    case BuildDeployActionTypes.CONFIGURE_LANDING_ZONE_STARTED:
      return {
        ...state,
        validateYaml: {
          loading: state.validateYaml.loading,
          data: {
            logs: '',
            planId: '',
          },
        },
      };
    case BuildDeployActionTypes.CONFIGURE_LANDING_ZONE_LOADING: {
      const { index, landingZoneName, csp, landingZoneObj, sector } =
        action.data as IConfigureLZLoadingPayload;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneId === landingZoneObj.landingZoneId) {
          return landingZoneObj;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones: landingZones,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_LANDING_ZONE_SUCCESS: {
      const name = action.data as string;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === name) {
          landingZone.loadingLandingZone = false;
          landingZone.landingZoneEnabled = true;
          landingZone.landingZoneConfigured = true;
          landingZone.landingZoneStatus = LandingZoneStatus.CREATED;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_LANDING_ZONE_ERROR: {
      const landingzonesErrorData = action.data as IConfigureLandingZoneError;
      return {
        ...state,
        error: landingzonesErrorData.error as Error,
        landingZones: [
          ...state.landingZones.slice(0, landingzonesErrorData.index),
          {
            ...state.landingZones[landingzonesErrorData.index],
            loadingLandingZone: false,
          },
          ...state.landingZones.slice(landingzonesErrorData.index + 1),
        ],
      };
    }
    case BuildDeployActionTypes.CONFIGURE_MICROSERVIVES_PATTERN_STARTED: {
      return {
        ...state,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_MICROSERVIVES_PATTERN_LOADING: {
      const { landingZoneName, microServiceId } = action.data as ICheckMicroServicesPatternStatus;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === landingZoneName) {
          landingZone.loadingMicroServicesWorkload = true;
          landingZone.workLoadPatternMicroServicesConfigured = false;
          landingZone.workLoadPatternMicroServicesId = microServiceId;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_MICROSERVIVES_PATTERN_SUCCESS: {
      const { landingZoneName, microServiceId } = action.data as ICheckMicroServicesPatternStatus;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === landingZoneName) {
          landingZone.loadingMicroServicesWorkload = false;
          landingZone.workLoadPatternMicroServicesConfigured = true;
          landingZone.workLoadPatternMicroServicesId = microServiceId;
          landingZone.workLoadPatternMicroServicesError = false;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_MICROSERVIVES_PATTERN_FAILED: {
      const landingZoneName = action.data as string;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === landingZoneName) {
          landingZone.loadingMicroServicesWorkload = false;
          landingZone.workLoadPatternMicroServicesConfigured = false;
          landingZone.workLoadPatternMicroServicesError = true;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_MICROSERVIVES_PATTERN_ERROR: {
      const { error, landingZoneName } = action.data as IConfigureMicroServiceError;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === landingZoneName) {
          landingZone.loadingMicroServicesWorkload = false;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_ETL_PATTERN_STARTED: {
      const etlStartedData = action.data as number;
      return {
        ...state,
        landingZones: [
          ...state.landingZones.slice(0, etlStartedData),
          {
            ...state.landingZones[etlStartedData],
            loadingEtlWorkload: true,
          },
          ...state.landingZones.slice(etlStartedData + 1),
        ],
      };
    }
    case BuildDeployActionTypes.CONFIGURE_ETL_PATTERN_SUCCESS: {
      const etlSuccessData = action.data as number;
      return {
        ...state,
        landingZones: [
          ...state.landingZones.slice(0, etlSuccessData),
          {
            ...state.landingZones[etlSuccessData],
            loadingEtlWorkload: false,
            workLoadPatternEtlConfigured: true,
          },
          ...state.landingZones.slice(etlSuccessData + 1),
        ],
      };
    }

    case BuildDeployActionTypes.DELETE_LANDING_ZONE_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.DESTROY_WORKLOAD_PATTERN_STARTED:
      return {
        ...state,
      };

    case BuildDeployActionTypes.GET_LANDING_ZONE_ERRORS_STARTED:
      return {
        ...state,
        loadingError: true,
      };

    case BuildDeployActionTypes.DOWNLOAD_ARCHITECTURE_DIAGRAM_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        architectureDiagram: {
          ...state.architectureDiagram,
          downloadContent: action.data as string,
        },
      };

    case BuildDeployActionTypes.FETCH_CATEGORY_AND_TECHDOMAIN_OPTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        architectureDiagram: {
          ...state.architectureDiagram,
          ...(action.data as IFetchCategoryAndTechDomainResponse),
        },
      };

    case BuildDeployActionTypes.GET_LANDING_ZONE_ERRORS_SUCCESS: {
      const landingZoneError = action.data as ILandingZoneErrorResponse;
      const landingZoneErrorArr: IBuildDeployErrorState[] = [];
      for (const [key, value] of Object.entries(landingZoneError)) {
        landingZoneErrorArr.push({
          errorKey: key,
          errorValue: value,
        });
      }
      return {
        ...state,
        loadingError: false,
        landingZonesError: landingZoneErrorArr,
      };
    }
    case BuildDeployActionTypes.CONFIGURE_LANDING_ERROR_SUCCESS: {
      const name = action.data as string;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === name) {
          landingZone.loadingLandingZone = false;
          landingZone.landingZoneEnabled = true;
          landingZone.landingZoneConfigured = false;
          landingZone.landingZoneStatus = LandingZoneStatus.ERROR;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.GET_LANDING_ZONE_ERRORS_FAILED:
      return {
        ...state,
        loadingError: false,
        error: action.data as Error,
      };
    case BuildDeployActionTypes.DESTROY_WORKLOAD_PATTERN_SUCCESS: {
      const destroyMsWorkLoadLandingZoneId = action.data as string;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneId === destroyMsWorkLoadLandingZoneId) {
          landingZone.workLoadPatternMicroServicesConfigured = false;
          landingZone.workLoadPatternMicroServicesEnabled = false;
          landingZone.workLoadPatternMicroServicesId = undefined;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
      };
    }
    case BuildDeployActionTypes.SET_TOTAL_ENVIRONMENTS:
      return {
        ...state,
        totalEnvironments: action.data as ICountLandingZone,
      };
    case BuildDeployActionTypes.SET_SELECTED_TAB:
      return {
        ...state,
        selectedTab: action.data as string,
      };
    case BuildDeployActionTypes.CREATE_LANDING_ZONE_YAML_SUCCESS:
      return {
        ...state,
        buildYaml: action.data as string,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_ALL_AVAILABLE_WORKLOAD_PATTERN_STARTED:
      return {
        ...state,
        loading: true,
        loadingCount: state.loadingCount + 1,
        allWorkloadPatternsAvailable: [],
      };
    case BuildDeployActionTypes.GET_ALL_AVAILABLE_WORKLOAD_PATTERN_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        allWorkloadPatternsAvailable: action.data as Array<IWorkloadPatternRespObj>,
      };
    case BuildDeployActionTypes.GET_ALL_ENVIRONMENTS_STARTED:
      return {
        ...state,
        loading: true,
        loadingCount: state.loadingCount + 1,
        allEnvironments: [],
      };
    case BuildDeployActionTypes.GET_ALL_ENVIRONMENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        allEnvironments: action.data as Array<IAllEnvironmentsRespObj>,
      };

    case BuildDeployActionTypes.ENVIRONMENTS_DEPLOYED_SUCCESS:
      return {
        ...state,
        environmentsDeployed: action.data as IEnvironmentsDeployed[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_ARCHITECTURE_LIST_SUCCESS:
      return {
        ...state,
        architectureList: action.data as IArchitectureCatalogueResponse[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.LANDING_ZONES_DEPLOYMENT_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        landingZoneDeploymentStatus: action.data as ILandingZoneDeploymentStatus[],
      };
    case BuildDeployActionTypes.LANDING_ZONES_DEPLOYMENT_STATUS_ERROR:
    case BuildDeployActionTypes.SET_ARCHTECTURE_DIAGRAM_SUCCESS:
    case BuildDeployActionTypes.SET_ARCHTECTURE_DIAGRAM_ERROR:
    case BuildDeployActionTypes.GET_PIPELINES_ANALYTICS_ERROR:
    case BuildDeployActionTypes.GET_PIPELINES_CUMMULATIVE_ANALYTICS_ERROR:
    case BuildDeployActionTypes.GET_YAML_CONTENT_ERROR:
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_CONTENT_ERROR:
    case BuildDeployActionTypes.GET_ARCHITECTURE_LIST_ERROR:
    case BuildDeployActionTypes.GET_INDUSTRY_YAML_CONTENT_ERROR:
    case BuildDeployActionTypes.GET_CLOUD_LANDING_ZONE_KEYS_ERROR:
    case BuildDeployActionTypes.SET_CLOUD_LANDING_ZONE_KEYS_ERROR:
    case BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_ERROR:
    case BuildDeployActionTypes.GET_MULTICLOUD_YAML_CONTENT_ERROR:
    case BuildDeployActionTypes.GET_ALL_AVAILABLE_WORKLOAD_PATTERN_ERROR:
    case BuildDeployActionTypes.CREATE_ENVIRONMENT_ERROR:
    case BuildDeployActionTypes.CREATE_LANDING_ZONE_YAML_ERROR:
    case BuildDeployActionTypes.ENVIRONMENTS_DEPLOYED_ERROR:
    case BuildDeployActionTypes.DEPLOY_LANDING_ZONE_LOADING:
    case BuildDeployActionTypes.CREATE_ENVIRONMENT_SUCCESS:
    case BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_DETAILED_LOGS_SUCCESS:
    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICIES_ERROR:
    case BuildDeployActionTypes.FETCH_INDUSTRY_COMPLIANCES_ERROR:
    case BuildDeployActionTypes.UPLOAD_ARCHITECTURE_SUCCESS:
    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICY_YAML_ERROR:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_SERVICES_ERROR:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_YAML_CONTENT_ERROR:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_POLLING_ERROR:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_PIPELINES_ANALYTICS_SUCCESS:
      return {
        ...state,
        pipelinesAnalytics: action.data as IPipelinesAnalyticsResponse[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_PIPELINES_CUMMULATIVE_ANALYTICS_SUCCESS:
      return {
        ...state,
        pipelinesCummulative: action.data as IPipelinesCummulativeAnalyticsResponse[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_YAML_CONTENT_SUCCESS:
      return {
        ...state,
        yamlContent: action.data as IYamlContent,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_CONTENT_SUCCESS:
      return {
        ...state,
        yamlDefaultTemplateContent: action.data as string,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_INDUSTRY_YAML_CONTENT_SUCCESS:
      return {
        ...state,
        industryYamlContent: action.data as string,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };

    case BuildDeployActionTypes.GET_CLOUD_LANDING_ZONE_KEYS_SUCCESS:
      return {
        ...state,
        cloudLandingZoneKeys: action.data as ICloudLandingZoneKeysResponse[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.SET_CLOUD_LANDING_ZONE_KEYS_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        landingZones: [action.data as ILandingZoneState, ...state.landingZones],
      };
    case BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_SUCCESS:
      return {
        ...state,
        deployInstanceId: action.data as string,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_THROUGH_CONSOLE_SUCCESS:
      return {
        ...state,
        status: action.data as string,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_STATUS_SUCCESS:
      return {
        ...state,
        cloudLandingZoneDeployStatus: action.data as ILandingZoneDeploymentResponse,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };

    case BuildDeployActionTypes.GET_DEPLOYMENT_DETAILS_SUCCESS:
      return {
        ...state,
        deployments: action.data as IDeploymentsResponse[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };

    case BuildDeployActionTypes.GET_DEPLOYMENT_AUDIT_STARTED:
      return {
        ...state,
        deploymentLogs: [] as IDeploymentLogsResponse[],
        loading: true,
        loadingCount: state.loadingCount + 1,
      };
    case BuildDeployActionTypes.GET_DEPLOYMENT_AUDIT_SUCCESS:
      return {
        ...state,
        deploymentLogs: action.data as IDeploymentLogsResponse[],
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_DEPLOY_INPUTS:
      return {
        ...state,
        deployValues: action.data as IDeployValueState,
      };
    case BuildDeployActionTypes.GET_MULTICLOUD_YAML_CONTENT_SUCCESS:
      return {
        ...state,
        multiCloudYamlContent: action.data as any,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_ARCH_DIAGRAM_STARTED:
      return {
        ...state,
        previewYamlBase64Loading: true,
      };
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_ARCH_DIAGRAM_SUCCESS:
      return {
        ...state,
        previewYamlBase64String: action.data as string,
        previewYamlBase64Loading: false,
      };
    case BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_ARCH_DIAGRAM_ERROR:
      return {
        ...state,
        previewYamlBase64Loading: false,
      };
    case BuildDeployActionTypes.DEPLOY_LANDING_ZONE_SUCCESS: {
      const name = action.data as string;
      const landingZones = state.landingZones.map((landingZone) => {
        if (landingZone.landingZoneName === name) {
          landingZone.loadingLandingZone = false;
          landingZone.landingZoneEnabled = true;
          landingZone.landingZoneConfigured = true;
          landingZone.landingZoneStatus = LandingZoneStatus.CREATED;
        }
        return landingZone;
      });
      return {
        ...state,
        landingZones,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    }
    case BuildDeployActionTypes.DEPLOY_LANDING_ZONE_ERROR: {
      const landingzonesErrorData = action.data as IConfigureLandingZoneError;
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
        error: landingzonesErrorData.error as Error,
        landingZones: [
          ...state.landingZones.slice(0, landingzonesErrorData.index),
          {
            ...state.landingZones[landingzonesErrorData.index],

            loadingLandingZone: false,
          },
          ...state.landingZones.slice(landingzonesErrorData.index + 1),
        ],
      };
    }

    case BuildDeployActionTypes.VALIDATE_CUSTOM_TEMPLATE_ZIP_FILE_STARTED:
      return {
        ...state,
        validateYaml: {
          loading: true,
          data: {
            logs: '',
            planId: '',
          },
        },
      };
    case BuildDeployActionTypes.VALIDATE_CUSTOM_TEMPLATE_ZIP_FILE_SUCCESS:
      return {
        ...state,
        loading: false,
        validateYaml: {
          loading: false,
          data: action.data as IValidateYamlPayload,
        },
      };
    case BuildDeployActionTypes.VALIDATE_CUSTOM_TEMPLATE_ZIP_FILE_ERROR:
      return {
        ...state,
        validateYaml: {
          ...state.validateYaml,
          loading: false,
        },
      };
    case BuildDeployActionTypes.VALIDATE_YAML_STARTED:
      return {
        ...state,
        validateYaml: {
          loading: true,
          data: {
            logs: '',
            planId: '',
          },
        },
      };
    case BuildDeployActionTypes.VALIDATE_YAML_FOR_CLOUD_STARTED:
      return {
        ...state,
        validateYaml: {
          loading: true,
          data: {
            logs: '',
            planId: '',
          },
        },
      };
    case BuildDeployActionTypes.VALIDATE_LANDING_ZONE_STARTED:
      return {
        ...state,
        loading: true,
        validateLandingZone: { validation: '' },
      };
    case BuildDeployActionTypes.VALIDATE_LANDING_ZONE_SUCCESS:
      return {
        ...state,
        loading: false,
        validateLandingZone: action.data as { validation: string },
      };
    case BuildDeployActionTypes.VALIDATE_LANDING_ZONE_ERROR:
    case BuildDeployActionTypes.VALIDATE_YAML_SUCCESS:
      return {
        ...state,
        loading: false,
        validateYaml: {
          loading: false,
          data: action.data as IValidateYamlPayload,
        },
      };
    case BuildDeployActionTypes.VALIDATE_YAML_ERROR:
      return {
        ...state,
        validateYaml: {
          ...state.validateYaml,
          loading: false,
        },
      };
    case BuildDeployActionTypes.FETCH_TERRAFORM_CODE_START:
    case BuildDeployActionTypes.FETCH_TERRAFORM_CODE_FOR_CLOUD_START:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_DOWNLOAD_STARTED:
      return {
        ...state,
        terraformCode: '',
        loadingCount: state.loadingCount + 1,
      };
    case BuildDeployActionTypes.FETCH_TERRAFORM_CODE_SUCCESS:
    case BuildDeployActionTypes.FETCH_TERRAFORM_CODE_ERROR:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_DOWNLOAD_SUCCESS:
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_DOWNLOAD_ERROR:
      return {
        ...state,
        terraformCode: action.data instanceof Error ? '' : (action.data as string),
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.VALIDATE_YAML_CLEAR:
      return {
        ...state,
        validateYaml: {
          loading: false,
          data: {
            logs: '',
            planId: '',
          },
        },
        terraformCode: '',
        industryPoliciesYaml: {} as {
          [key: string]: string;
        },
      };

    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICIES_SUCCESS:
      return {
        ...state,
        industryPolicies: action.data as Array<IIndustryPolicy>,
        loadingCount: Math.max(0, state.loadingCount - 1),
        loading: false,
      };

    case BuildDeployActionTypes.FETCH_INDUSTRY_COMPLIANCES_SUCCESS:
      return {
        ...state,
        industryCompliances: action.data as Array<IIndustryCompliance>,
        loadingCount: Math.max(0, state.loadingCount - 1),
        loading: false,
      };

    case BuildDeployActionTypes.DOWNLOAD_ARCHITECTURE_DIAGRAM_CLEAR:
      return {
        ...state,
        architectureDiagram: {
          ...state.architectureDiagram,
          downloadContent: '',
        },
      };

    case BuildDeployActionTypes.FETCH_INDUSTRY_POLICY_YAML_SUCCESS:
      return {
        ...state,
        industryPoliciesYaml: action.data as {
          [key: string]: string;
        },
        loadingCount: Math.max(0, state.loadingCount - 1),
        loading: false,
      };

    case BuildDeployActionTypes.BUILD_DEPLOY_HARD_RELOAD:
      return {
        ...state,
        loading: false,
        loadingCount: 0,
        loadingError: false,
        previewYamlBase64Loading: false,
        validateYaml: {
          ...state.validateYaml,
          loading: false,
        },
        deployValues: {
          cloudEnvName: '',
          environmentName: '',
          environmentType: '',
          selectedProvider: CloudServiceProvider.AWS,
          selectedEnvType: envTypes.DEFAULT_TEMPLATE.name,
          defaultProviderSelected: false,
          multiCloudCluster: { 'default': ['XYZ'] },
          projectName: '',
          multiActiveCloud: 'default',
          multiCloudClusterForm: {},
          highlightedCluster: ['anthos_gcp'],
          industryYamlPolicies: [],
          iac: 'terraform',
        } as IDeployValueState,
        industryPolicies: [],
        industryCompliances: [],
      };

    case BuildDeployActionTypes.GET_GEN_AI_CREATE_SERVICES_SUCCESS:
      return {
        ...state,
        loadingCount: Math.max(0, state.loadingCount - 1),
        loading: false,
        genAiCreate: {
          ...state.genAiCreate,
          services: action.data as Record<string, Array<string>>,
        },
      };

    case BuildDeployActionTypes.GET_GEN_AI_CREATE_YAML_CONTENT_SUCCESS:
      return {
        ...state,
        loadingCount: Math.max(0, state.loadingCount - 1),
        loading: false,
        genAiCreate: {
          ...state.genAiCreate,
          terraformFolderName: (action.data as IGenAiCreateResponse).terraformFolderName,
        },
      };
    case BuildDeployActionTypes.GET_GEN_AI_CREATE_POLLING_SUCCESS:
      return {
        ...state,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };
    case BuildDeployActionTypes.GET_WORKFLOW_LOGS_SUCCESS:
      return {
        ...state,
        workFlowLogs: action.data as IWorkFLowLogsResponse,
        loading: false,
        loadingCount: Math.max(0, state.loadingCount - 1),
      };

    case BuildDeployActionTypes.UPLOAD_CUSTOM_TEMPLATE_ZIP_FILE_STARTED:
      return {
        ...state,
        loading: true,
        customCloudTemplateDeploymentInfo: {
          ...state.customCloudTemplateDeploymentInfo,
          uploadedZipFile: (action.data as IUploadCustomCloudConfigurationFilePayload).zip_file,
        },
      };
    case BuildDeployActionTypes.UPLOAD_CUSTOM_TEMPLATE_ZIP_FILE_SUCCESS:
      const data = action.data as IUploadCustomCloudConfigurationFileResponse;
      return {
        ...state,
        loading: false,
        customCloudTemplateDeploymentInfo: {
          ...state.customCloudTemplateDeploymentInfo,
          branch: data.branch,
          landingZoneId: data.landingZoneId,
          uploadedZipFile: state.customCloudTemplateDeploymentInfo.uploadedZipFile,
        },
      };
    case BuildDeployActionTypes.UPLOAD_CUSTOM_TEMPLATE_ZIP_FILE_ERROR:
      return {
        ...state,
        loading: false,
        error: action.data,
        customCloudTemplateDeploymentInfo: {
          ...state.customCloudTemplateDeploymentInfo,
          uploadedZipFile: null,
        },
      };
    case BuildDeployActionTypes.GET_CLOUD_BUILD_CONFIG_VARIABLES_SUCCESS:
      return {
        ...state,
        loading: false,
        cloudBuildConfigVariables: action.data as ICloudBuildConfigVariable[],
      };
    case BuildDeployActionTypes.GET_CLOUD_BUILD_CONFIG_VARIABLES_ERROR:
      return {
        ...state,
        loading: false,
      };
    case BuildDeployActionTypes.GENERATE_ARCHITECTURE_DIAGRAMS_SUCCESS: {
      const newDiagram = action.data as IGeneratedArchitectureDiagramHistoryStackItem;
      if (!newDiagram) {
        return state;
      }

      // Create new stack item for the generated diagram
      const newStackItem: IGeneratedArchitectureDiagramHistoryStackItem = {
        diagramType: newDiagram.diagramType,
        content: newDiagram.content,
        ...(newDiagram.imageUrl && { imageUrl: newDiagram.imageUrl }),
        techPlatform: newDiagram.techPlatform,
        architectureName: newDiagram.architectureName,
        category: newDiagram.category,
        techDomain: newDiagram.techDomain,
        description: newDiagram.description,
        relevantUsecase: newDiagram.relevantUsecase,
      };

      // Update the current diagram based on type
      // Efficiently update the diagram using object spread
      const updatedDiagram = {
        ...state.generatedArchitectures.generatedArchitectureDiagram,
        ...newDiagram,
      };

      // Truncate stack at current position and add new item
      const currentHistory = state.generatedArchitectures.generatedArchitectureDiagramHistory;
      const truncatedStack = currentHistory.stack.slice(0, currentHistory.currentIndex + 1);

      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: false,
          generatedArchitectureDiagram: updatedDiagram,
          generatedArchitectureDiagramHistory: {
            stack: [...truncatedStack, newStackItem],
            currentIndex: truncatedStack.length,
          },
        },
      };
    }

    case BuildDeployActionTypes.REVISE_GENERATED_ARCHITECTURE_DIAGRAMS_SUCCESS: {

      const revisedDiagram = action.data as IGenerateArchitectureDiagramsResponse;

      // Create new stack item for the revised diagram (same as updated diagram)
      const newStackItem: IGeneratedArchitectureDiagramHistoryStackItem = {
        ...state.generatedArchitectures.generatedArchitectureDiagram,
        ...revisedDiagram,
      };

      // Truncate stack at current position and add new item
      const currentHistory = state.generatedArchitectures.generatedArchitectureDiagramHistory;
      const truncatedStack = currentHistory.stack.slice(0, currentHistory.currentIndex + 1);

      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: false,
          generatedArchitectureDiagram: newStackItem,
          generatedArchitectureDiagramHistory: {
            stack: [...truncatedStack, newStackItem],
            currentIndex: truncatedStack.length,
          },
        },
      };
    }
    
    case BuildDeployActionTypes.CONVERT_GENERATED_ARCHITECTURE_DIAGRAMS_SUCCESS: {

      const convertedDiagram = action.data as IConvertArchitectureDiagramsResponseWithType;

      // Create new stack item for the revised diagram (same as updated diagram)
      const newStackItem: IGeneratedArchitectureDiagramHistoryStackItem = {
        ...state.generatedArchitectures.generatedArchitectureDiagram,
        content: convertedDiagram.content,
        diagramType: convertedDiagram.diagramType,
        imageUrl: convertedDiagram.imageUrl,
      };

      // Truncate stack at current position and add new item
      const currentHistory = state.generatedArchitectures.generatedArchitectureDiagramHistory;
      const truncatedStack = currentHistory.stack.slice(0, currentHistory.currentIndex + 1);

      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: false,
          generatedArchitectureDiagram: newStackItem,
          generatedArchitectureDiagramHistory: {
            stack: [...truncatedStack, newStackItem],
            currentIndex: truncatedStack.length,
          },
        },
      };
    }

    case BuildDeployActionTypes.UNDO_DIAGRAM: {
      const history = state.generatedArchitectures.generatedArchitectureDiagramHistory;
      if (history.currentIndex > 0) {
        const newIndex = history.currentIndex - 1;
        const previousItem = history.stack[newIndex];
        return {
          ...state,
          generatedArchitectures: {
            ...state.generatedArchitectures,
            generatedArchitectureDiagram: previousItem,
            generatedArchitectureDiagramHistory: {
              ...history,
              currentIndex: newIndex,
            },
          },
        };
      }
      return state;
    }

    case BuildDeployActionTypes.REDO_DIAGRAM: {
      const history = state.generatedArchitectures.generatedArchitectureDiagramHistory;
      if (history.currentIndex < history.stack.length - 1) {
        const newIndex = history.currentIndex + 1;
        const nextItem = history.stack[newIndex];

        return {
          ...state,
          generatedArchitectures: {
            ...state.generatedArchitectures,
            generatedArchitectureDiagram: nextItem,
            generatedArchitectureDiagramHistory: {
              ...history,
              currentIndex: newIndex,
            },
          },
        };
      }
      return state;
    }

    case BuildDeployActionTypes.UPLOAD_GENERATED_ARCHITECTURE_DIAGRAM_STARTED:
    case BuildDeployActionTypes.CLEAR_DIAGRAM_HISTORY_AND_CURRENT: {
      return {
        ...state,
        generatedArchitectures: {
          ...state.generatedArchitectures,
          generatedArchitecturesLoading: false,
          generatedArchitectureDiagram: {
            content:'',
            imageUrl: '',
            techPlatform: '',
            architectureName: '',
            category: '',
            techDomain: '',
            description: '',
            relevantUsecase: '',
            diagramType: 'mermaid'
          },
          generatedArchitectureDiagramHistory: {
            stack: [],
            currentIndex: -1,
          },
        },
      };
    }
    case BuildDeployActionTypes.GET_CLOUD_BUILD_CONFIG_VARIABLES_STARTED:
    case BuildDeployActionTypes.GET_CLOUD_BUILD_CONFIG_VARIABLES_STARTED:
      return state;
    default:
      return state;
  }
};
